import { inject, Injectable } from '@angular/core';
import { PortalFieldConfig, uppercaseParser } from '@portal/form/data-access';
import { convertToBehaviorSubject } from '@portal/util';
import { debounceTime, distinctUntilChanged, EMPTY, map, startWith, tap, withLatestFrom } from 'rxjs';
import { initCountryOptions } from '../const/app.const';
import { CountryFacade, PersonnelFacade } from '../store';
import { CommonReportingStandardModelKeys, CrsTaxResidenceKeys } from '../const/form-field-key.const';
import { switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CrsFormConfigService {
  private readonly countryFacade = inject(CountryFacade);
  private readonly countryOptions$ = convertToBehaviorSubject(this.countryFacade.allCountry$.pipe(map(v => initCountryOptions(v))), []);
  private readonly personnel$ = inject(PersonnelFacade).selectedPersonnel$;

  initCommonReportingStandardFormFields(): PortalFieldConfig[] {
    return [
      {
        template: `
            <p class="mb-3">
              See the Tax Residence Information
              <a
                href="https://www.cimb.com.sg/en/business/help-support/regulations-policies/common-reporting-standards.html"
                class="text-primary underline"
                target="_blank"
                >here</a
              >
            </p>
        `,
      },
      {
        key: CommonReportingStandardModelKeys.crs,
        type: 'form-array-crs',
        props: { minLength: 1 },
        hooks: {
          onInit: field => {
            const control = field.formControl;
            return control?.valueChanges.pipe(
              startWith(control?.value),
              debounceTime(100),
              map(() => control?.getRawValue()),
              distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
              tap(value => {
                if (!field.props) return;
                const countryIds = value?.map((v: object) => v[CrsTaxResidenceKeys.countryId]).filter(Boolean) ?? [];
                const options =
                  this.countryOptions$.value?.map(option => {
                    option.disabled = countryIds.includes(option.value);
                    return option;
                  }) ?? [];
                this.countryOptions$.next(options);
              }),
            );
          },
        },

        fieldArray: {
          fieldGroupClassName: 'field-columns',
          fieldGroup: [
            {
              key: CrsTaxResidenceKeys.countryId,
              type: 'mat-select',
              props: {
                label: 'Country of Tax Residence',
                placeholder: 'Please Select',
                options: this.countryOptions$,
              },
              hooks: {
                // TODO: enhance this hooks
                onInit: field => {
                  const control = field.formControl;
                  return control?.valueChanges.pipe(
                    startWith(control?.value),
                    debounceTime(100),
                    withLatestFrom(this.countryFacade.singaporeCountry$),
                    switchMap(([countryId, singaporeCountry]) => {
                      return this.personnel$.pipe(
                        tap(personnel => {
                          if (!field.options?.formState?.isMyInfo || !personnel || !personnel.data.nricNumber) return;

                          if (field.options?.formState?.isMyInfo && countryId === singaporeCountry?.id) {
                            field.form?.get(CrsTaxResidenceKeys.tin)?.setValue(personnel.data.nricNumber);
                          }
                        }),
                      );
                    }),
                  );
                },
              },
            },
            {
              key: CrsTaxResidenceKeys.tin,
              type: 'input',
              validators: { validation: ['tin'] },
              parsers: [uppercaseParser],
              props: {
                label: 'Tax Identification Number (TIN)',
                placeholder: 'e.g. 1234567A',
                // required: true,
                maskConfig: { mask: 'A*' },
              },
              hooks: {
                onInit: field => {
                  const parentControl = field.parent?.formControl;
                  return parentControl?.valueChanges.pipe(
                    startWith(parentControl?.value),
                    debounceTime(100),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                    withLatestFrom(this.countryFacade.singaporeCountry$),
                    switchMap(([_, singaporeCountry]) => {
                      if (!field?.options?.formState?.isMyInfo || field?.model?.countryId !== singaporeCountry?.id) {
                        field?.formControl?.enable();
                      } else {
                        field?.formControl?.disable();
                      }
                      return EMPTY;
                    }),
                  );
                },
              },
            },
          ],
        },
      },
    ];
  }
}
